import axios from 'axios';
import {
  DEVICE_ID_HEADER,
  getDeviceIdentityHeaders,
  isValidDeviceId,
  storeDeviceId,
} from './deviceIdentity';

const baseURL = import.meta.env.VITE_API_BASE || import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const AUTH_STORAGE_KEY = 'token';
export const AUTH_EXPIRED_EVENT = 'oakley:auth-expired';

let authTokenProvider = null;

const createApiClient = () => axios.create({
  baseURL,
  withCredentials: true,
});

export const publicApi = createApiClient();
const api = createApiClient();

// CSRF token management
let csrfToken = null;
let csrfTokenPromise = null;

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

export const getStoredAuthToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredAuthToken = (token) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (token) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  } catch {
    // Ignore storage write failures and allow the request path to continue.
  }
};

export const clearStoredAuthToken = () => {
  setStoredAuthToken(null);
};

export const setApiAuthTokenProvider = (provider) => {
  authTokenProvider = typeof provider === 'function' ? provider : null;
};

export const isJwtExpired = (token, skewSeconds = 30) => {
  const payload = decodeJwtPayload(token);
  const expiresAt = payload?.exp;

  if (!expiresAt || typeof expiresAt !== 'number') {
    return false;
  }

  return expiresAt <= Math.floor(Date.now() / 1000) + skewSeconds;
};

export const getValidStoredAuthToken = () => {
  const token = getStoredAuthToken();

  if (!token) {
    return null;
  }

  if (isJwtExpired(token)) {
    clearStoredAuthToken();
    return null;
  }

  return token;
};

const resolveAuthorizationToken = async () => {
  if (authTokenProvider) {
    try {
      const providedToken = await authTokenProvider();
      if (providedToken) {
        return providedToken;
      }
    } catch {
      // Fall back to the legacy token path so admin auth remains resilient.
    }
  }

  return getValidStoredAuthToken();
};

const emitAuthExpired = (reason = 'expired') => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, {
    detail: { reason },
  }));
};

const syncDeviceIdentityFromResponse = (headers) => {
  const returnedDeviceId =
    headers?.[DEVICE_ID_HEADER] ||
    headers?.[DEVICE_ID_HEADER.toLowerCase()];

  if (isValidDeviceId(returnedDeviceId)) {
    storeDeviceId(returnedDeviceId);
  }
};

// Fetch CSRF token on initialization
const fetchCsrfToken = async () => {
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }

  try {
    csrfTokenPromise = axios
      .get(`${baseURL}/auth/csrf-token`, {
        withCredentials: true,
        headers: getDeviceIdentityHeaders(),
      })
      .then((response) => {
        csrfToken = response.data.csrfToken;
        return csrfToken;
      })
      .catch(() => {
        csrfToken = null;
        return null;
      })
      .finally(() => {
        csrfTokenPromise = null;
      });

    return await csrfTokenPromise;
  } catch {
    csrfTokenPromise = null;
    return null;
  }
};

// Initialize CSRF token
void fetchCsrfToken();

const applySharedInterceptors = (client, { attachAuth = false } = {}) => {
  client.interceptors.request.use(
    async (config) => {
      const nextConfig = { ...config };
      nextConfig.headers = nextConfig.headers || {};
      const deviceIdentityHeaders = getDeviceIdentityHeaders();

      if (deviceIdentityHeaders[DEVICE_ID_HEADER] && !nextConfig.headers[DEVICE_ID_HEADER]) {
        nextConfig.headers[DEVICE_ID_HEADER] = deviceIdentityHeaders[DEVICE_ID_HEADER];
      }

      if (attachAuth && nextConfig.skipAuth !== true) {
        const token = await resolveAuthorizationToken();

        if (token) {
          nextConfig.headers.Authorization = `Bearer ${token}`;
        } else if (nextConfig.headers.Authorization) {
          delete nextConfig.headers.Authorization;
        }
      }

      if (['post', 'put', 'patch', 'delete'].includes(nextConfig.method?.toLowerCase())) {
        if (!csrfToken) {
          await fetchCsrfToken();
        }

        if (csrfToken) {
          nextConfig.headers['x-csrf-token'] = csrfToken;
        }
      }

      return nextConfig;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => {
      syncDeviceIdentityFromResponse(response.headers);
      return response;
    },
    async (error) => {
      const originalRequest = error.config || {};
      const csrfCode = error.response?.data?.code;

      syncDeviceIdentityFromResponse(error.response?.headers);

      if (
        error.response?.status === 403 &&
        !originalRequest._csrfRetry &&
        (csrfCode === 'CSRF_TOKEN_INVALID' || csrfCode === 'CSRF_TOKEN_MISSING')
      ) {
        originalRequest._csrfRetry = true;
        await fetchCsrfToken();

        if (csrfToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['x-csrf-token'] = csrfToken;
          return client(originalRequest);
        }
      }

      if (
        attachAuth &&
        error.response?.status === 401 &&
        originalRequest.skipAuth !== true &&
        !originalRequest._authExpiredHandled
      ) {
        const storedToken = getStoredAuthToken();
        const hadAuthenticatedRequest = Boolean(originalRequest.headers?.Authorization || storedToken);

        if (hadAuthenticatedRequest) {
          originalRequest._authExpiredHandled = true;
          clearStoredAuthToken();
          emitAuthExpired(isJwtExpired(storedToken) ? 'expired' : 'unauthorized');
        }
      }

      return Promise.reject(error);
    }
  );
};

applySharedInterceptors(publicApi);
applySharedInterceptors(api, { attachAuth: true });

export default api;
